// TanStack Query hooks: one per server resource.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useSyncExternalStore } from "react";
import { api, isLoggedIn, onAuthChange, type Me, type S } from "./client";

export function useLoggedIn(): boolean {
  return useSyncExternalStore(onAuthChange, isLoggedIn);
}

export function useMe() {
  const loggedIn = useLoggedIn();
  return useQuery({
    queryKey: ["me", loggedIn],
    queryFn: () => (loggedIn ? api<Me>("/me") : Promise.resolve(null)),
    staleTime: 30_000,
  });
}

export function usePatchMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["MePatch"]) => api<Me>("/me", { method: "PATCH", body }),
    onSuccess: (me) => qc.setQueryData(["me", true], me),
  });
}

export type Dashboard = {
  display_name: string;
  needs_seed: boolean;
  rating: {
    r: number | null;
    rd: number;
    delta_7d: number | null;
    peak: number | null;
    rated_count: number;
    placement: { index: number; total: number } | null;
  } | null;
  solved: number;
  solved_this_week: number;
  accuracy_30d: number | null;
  accuracy_spark: (number | null)[];
  streak_days: number;
  best_streak_days: number;
  rating_history: { t: number; r: number; rd: number }[];
  activity: Record<string, number>;
  by_theme: { theme: string; solved: number; total: number; rate: number }[];
  by_band: { band: number; solved: number; total: number; accuracy: number; avg_ms: number | null }[];
  weekly: { week: string; solved: number; failed: number }[];
  review_due: number;
  favorites: number;
  modules_in_progress: ModuleInfo[];
  badges: string[];
};

export type ModuleInfo = {
  id: number;
  slug: string;
  title: string;
  description: string;
  visibility: string;
  rated: boolean;
  ordered: boolean;
  total: number;
  solved: number;
  avg_rating: number | null;
  cover: string | null;
};

export const useDashboard = () =>
  useQuery({ queryKey: ["dashboard"], queryFn: () => api<Dashboard>("/me/dashboard") });

/** Labels of the exercises the user can open, for "train only this label". */
export const useLabels = () =>
  useQuery({ queryKey: ["labels"], queryFn: () => api<S["LabelsOut"]>("/labels"), staleTime: 5 * 60_000 });

export const useModules = (enabled = true) =>
  useQuery({ queryKey: ["modules"], queryFn: () => api<ModuleInfo[]>("/me/modules"), enabled });

export const useLockedModules = (enabled = true) =>
  useQuery({
    queryKey: ["modules-locked"],
    queryFn: () => api<{ id: number; title: string; description: string }[]>("/me/modules-locked"),
    enabled,
  });

/** Are there modules to show at all (open, or locked behind a code)? Without
 * any, the exercise database is all there is and the Modules tab stays away. */
export function useHasModules(): boolean {
  const loggedIn = useLoggedIn();
  const open = useModules(loggedIn);
  const locked = useLockedModules(loggedIn);
  return loggedIn && ((open.data?.length ?? 0) > 0 || (locked.data?.length ?? 0) > 0);
}

export type ModuleDetail = ModuleInfo & {
  exercises: { id: number; textfen: string; rating: number; state: "solved" | "failed" | "new" }[];
};
export const useModule = (id: number) =>
  useQuery({ queryKey: ["module", id], queryFn: () => api<ModuleDetail>(`/me/modules/${id}`) });

export const useFavorites = () =>
  useQuery({ queryKey: ["favorites"], queryFn: () => api<S["FavoriteRow"][]>("/me/favorites") });

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      api(`/me/favorites/${id}`, { method: on ? "PUT" : "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorites"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export type HistoryPage = { items: S["AttemptRow"][]; page: number; pages: number; total: number };
export const useHistory = (params: Record<string, string | number | undefined>) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== "") as [string, string][],
  ).toString();
  return useQuery({
    queryKey: ["history", qs],
    queryFn: () => api<HistoryPage>(`/me/attempts?${qs}`),
    placeholderData: (prev) => prev,
  });
};

export type Board = {
  rows: { rank: number; user_id: number; display_name: string; club: string | null; country: string | null; score?: number; solved?: number; seconds?: number }[];
  me: Board["rows"][number] | null;
  total: number;
  scope_missing?: string;
};
export const useLeaderboard = (board: string, scope: string, moduleId?: number) =>
  useQuery({
    queryKey: ["leaderboard", board, scope, moduleId],
    queryFn: () =>
      api<Board>(`/leaderboard/${board}?scope=${scope}${moduleId ? `&module_id=${moduleId}` : ""}`),
    enabled: board !== "module" || !!moduleId,
  });

/** A value that follows `value` after `ms` without changes. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
